'use client';

import { logger } from '@/lib/utils/logger';
import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { AnimatedTitle } from '../../animated-title';

import type { Nftcollection } from '@/types';
import { NotificationPopup } from './notification-popup';

export interface collection extends Nftcollection {
  tags?: string[];
  cats?: string[];
  isDrop?: boolean;
}
interface DropHeroSectionProps {
  collection: collection;
}

export const Landscape_HeroSection: React.FC<DropHeroSectionProps> = ({ collection }) => {
  const title = collection.name;

  const description =
    collection.description && collection.description.length > 250
      ? `${collection.description.slice(0, 250)}...`
      : collection.description;

  const tags =
    [
      ...(collection.tags && collection.tags.length > 0 ? collection.tags : []),
      ...(collection.cats && collection.cats.length > 0 ? collection.cats : []),
    ].length > 0
      ? [
          ...(collection.tags && collection.tags.length > 0 ? collection.tags : []),
          ...(collection.cats && collection.cats.length > 0 ? collection.cats : []),
        ]
      : [];

  const leftTop = collection?.cats && collection.cats.length > 0 ? collection.cats[0] : '';
  const rightTop = `SERIES OF ${collection.totalSupply} ARTWORKS`;

  // COUNT DOWN
  // Add this function to calculate the countdown
  const calculateCountdown = (startDate: Date) => {
    const now = new Date();
    const timeRemaining = startDate.getTime() - now.getTime();

    if (timeRemaining <= 0) {
      return { hasWeeks: false, countdown: '00:00:00:00' };
    }

    // Calculate time units
    const minutes = Math.floor((timeRemaining / (1000 * 60)) % 60);
    const hours = Math.floor((timeRemaining / (1000 * 60 * 60)) % 24);
    const days = Math.floor((timeRemaining / (1000 * 60 * 60 * 24)) % 7);
    const weeks = Math.floor(timeRemaining / (1000 * 60 * 60 * 24 * 7));

    const hasWeeks = weeks > 0;

    // Format the countdown string with padding
    let countdown: string;
    if (hasWeeks) {
      countdown = `${weeks.toString().padStart(2, '0')}:${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      countdown = `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    return { hasWeeks, countdown };
  };

  // Add this state to store the countdown
  const [countdownState, setCountdownState] = useState({ hasWeeks: true, countdown: '00:00:00:00' });

  // Add this effect to update the countdown
  useEffect(() => {
    if (!collection.isDrop || !collection.startDate) return undefined;

    const updateCountdown = () => {
      const result = calculateCountdown(new Date(collection.startDate));
      setCountdownState(result);
    };

    // Initial update
    updateCountdown();

    // Update every minute
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [collection.startDate, collection.isDrop]);

  // In the component:
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  const handleEmailSubmit = (email: string) => {
    // Add your email submission logic here
    logger.log('Email submitted:', email);
    setShowNotifyModal(false);
  };

  return (
    <section className="w-full">
      <div className="p-[15px] rounded-md bg-white mb-[12px] flex flex-col justify-between relative h-auto lg:h-[325px]">
        <div className="static lg:absolute top-2 left-2">
          <AnimatedTitle marginClass="mb-1">{leftTop}</AnimatedTitle>
        </div>
        <div className="absolute top-2 right-2">
          <AnimatedTitle marginClass="mb-1">{rightTop}</AnimatedTitle>
        </div>

        {/* Add the modal */}
        {showNotifyModal && (
          <NotificationPopup
            isOpen={showNotifyModal}
            onClose={() => setShowNotifyModal(false)}
            title={title}
            date={collection.startDate}
            onEmailSubmit={handleEmailSubmit}
          />
        )}

        {/* Title positioned in the center of the left side */}
        <div className="static lg:absolute left-3 top-1/2 transform lg:-translate-y-1/2 my-2">
          <h1 className="text-[24px] md:text-[30px] font-superior uppercase text-black">{title}</h1>
        </div>

        {/* Countdown timer positioned aligned with tags */}
        {collection.isDrop ? (
          <div className="static lg:relative left-3 top-1/2 transform lg:-translate-y-1/2 m-auto my-2 mb-4 lg:mb-0">
            <div className="flex space-x-4 font-superior text-black">
              <div className={`grid grid-cols-${countdownState.hasWeeks ? 4 : 3}`}>
                <div
                  className={`flex flex-col items-center mb-2 tracking-widest col-span-${countdownState.hasWeeks ? 4 : 3}`}
                >
                  <div className="text-[30px] font-normal">{countdownState.countdown}</div>
                </div>
                {countdownState.hasWeeks ? (
                  <>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ WEEKS ]</div>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ DAYS ]</div>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ HOURS ]</div>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ MINUTES ]</div>
                  </>
                ) : (
                  <>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ DAYS ]</div>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ HOURS ]</div>
                    <div className="text-[14px] text-[9px] font-mono col-span-1 text-center">[ MINUTES ]</div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          ''
        )}
        {/* Description section - absolutely positioned */}
        <div className="static lg:absolute bottom-3 left-3 max-w-lg mb-4">
          <p className="text-[10px] font-mono text-gray-900 mb-2">[ DESCRIPTION ]</p>
          <p className="text-[10px] font-mono text-gray-500 leading-relaxed">{description}</p>
        </div>
        {/* Tags section - absolutely positioned */}
        {tags.length > 0 ? (
          <div className="static lg:absolute bottom-3" style={{ left: 'calc(50% - 100px)' }}>
            <p className="text-[10px] font-mono text-gray-900 mb-2">[ TAGS ]</p>
            <div className="flex flex-wrap gap-1 max-w-lg">
              {tags.map((tag, index) => (
                <a
                  key={index}
                  className={`text-[10px] ${tag === 'ETHEREUM' ? 'bg-gray-300 hover:bg-gray-200' : 'bg-gray-300 hover:bg-gray-200'} px-2 py-1 rounded-md cursor-pointer transition-colors`}
                >
                  {tag}
                </a>
              ))}
            </div>
          </div>
        ) : (
          ''
        )}

        {collection.isDrop && (
          <div>
            <button
              onClick={() => setShowNotifyModal(true)}
              className="text-[11px] bg-black h-[24px]
   inline-flex font-bold items-center px-2 py-1 rounded-sm text-white hover:text-white/80 transition-colors duration-200
  static lg:absolute mt-4 bottom-3 right-3 cursor-pointer"
            >
              <Bell className="h-3 w-3 mr-1" /> GET NOTIFIED
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
